import {
  autocompletion,
  closeBrackets,
  completionStatus,
} from '@codemirror/autocomplete';
import { history } from '@codemirror/commands';
import {
  bracketMatching,
  foldGutter,
  syntaxHighlighting,
} from '@codemirror/language';
import { linter } from '@codemirror/lint';
import { highlightSelectionMatches } from '@codemirror/search';
import { EditorState, Extension } from '@codemirror/state';
import {
  closeHoverTooltips,
  EditorView,
  gutter,
  GutterMarker,
  hasHoverTooltips,
  highlightActiveLine,
  highlightActiveLineGutter,
  lineNumbers,
  rectangularSelection,
  tooltips,
} from '@codemirror/view';
import { classHighlighter } from '@lezer/highlight';
import { type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';

import { Text } from '@dynatrace/strato-components/typography';
import Borders from '@dynatrace/strato-design-tokens/borders';
import BoxShadows from '@dynatrace/strato-design-tokens/box-shadows';
import Colors from '@dynatrace/strato-design-tokens/colors';
import Spacings from '@dynatrace/strato-design-tokens/spacings';
import Typography from '@dynatrace/strato-design-tokens/typography';
import { ChevronDownSmallIcon } from '@dynatrace/strato-icons';

import type { Suggestion } from '../auto-completion/autocompletion.extension.js';
import { autocompletionTypeTextCSS } from '../BaseCodeEditor.sty.js';
import { baseCodeEditorContentTestId, dataTestId } from '../testIds.js';
import type { IntlProps } from '../utils/render-node.js';
import { render } from '../utils/render-node.js';

const Source = (source: string) => () =>
  (
    <Text fontStyle="code" textStyle="small-emphasized" style={{ width: 30 }}>
      {source}
    </Text>
  );

const Type = (type: string) => () =>
  (
    <Text
      fontStyle="code"
      textStyle="base"
      as="span"
      className={autocompletionTypeTextCSS}
    >
      {type}
    </Text>
  );

// add options object + required
export const getDefaultConfiguration = (
  intlProps: IntlProps,
  options: { required: boolean; size: 'default' | 'condensed' } = {
    required: false,
    size: 'default',
  },
): Extension[] => {
  const isSizeDefault = options.size === 'default';
  return [
    history(),
    EditorState.allowMultipleSelections.of(true),
    bracketMatching(),
    closeBrackets(),
    autocompletion({
      closeOnBlur: false,
      icons: false,
      optionClass: (completion) => {
        return completion.type ? `cm-completionOption-${completion.type}` : '';
      },
      addToOptions: [
        {
          position: 21,
          render: (completion: Suggestion) =>
            render(
              completion,
              typeof completion.prefix === 'string'
                ? Source(completion.prefix)
                : completion.prefix,
              intlProps,
            ),
        },
        {
          position: 51,
          render: (completion: Suggestion) =>
            render(
              completion,
              typeof completion.postfix === 'string'
                ? Type(completion.postfix)
                : completion.postfix,
              intlProps,
            ),
        },
      ],
    }),
    linter(() => []), // empty linter to enable theme for linter errors
    syntaxHighlighting(classHighlighter),
    rectangularSelection(),
    highlightActiveLine(),
    highlightActiveLineGutter(),
    highlightSelectionMatches(),
    EditorView.contentAttributes.of({
      [dataTestId]: baseCodeEditorContentTestId,
      'aria-haspopup': 'listbox',
      'aria-required': options.required ? 'true' : 'false',
    }),
    EditorView.updateListener.of((update) => {
      // prevent hover tooltips and autocompletion to be open at the same time
      // if hover tooltips are visible and the autocompletion was opened
      // then close the hover tooltips
      if (
        hasHoverTooltips(update.state) &&
        completionStatus(update.state) !== null
      ) {
        update.view.dispatch({
          effects: closeHoverTooltips,
        });
      }
    }),
    // By default the `DQLEditor` tooltip has `position: fixed` except for when the editor is placed
    // inside a transformed element. In that case, the tooltip has `position: absolute` and is cut off
    // in some scenarios due to the new stacking context created by the transformed element.
    // This extension ensures that the tooltip remains visible / is never cut off.
    tooltips({ parent: document.body }),
    EditorView.baseTheme({
      '.cm-tooltip': {
        background: `${Colors.Background.Surface.Default} !important`,
        color: Colors.Text.Neutral.Default,
        maxWidth: '500px',
        padding: Spacings.Size4,
        borderRadius: Borders.Radius.Surface.Subdued,
        boxShadow: BoxShadows.Surface.Floating.Rest,
        border: 'none !important',
      },

      '.cm-tooltip.cm-tooltip-autocomplete': {
        width: '500px',
      },

      '.cm-tooltip-below': {
        marginTop: ` ${Spacings.Size4}`,
      },

      '.cm-tooltip.cm-tooltip-autocomplete > ul > completion-section': {
        whiteSpace: 'normal',
        textOverflow: 'initial',
        wordBreak: 'normal',
        overflowWrap: 'anywhere',
      },

      '.cm-tooltip.cm-tooltip-autocomplete > ul > completion-section:has(+ li[role="option"].cm-empty-option)':
        {
          borderBottom: '0 !important',
        },

      '.cm-tooltip-autocomplete > ul > li[role="option"]': {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: Spacings.Size4,
        padding: `${Spacings.Size6} ${Spacings.Size12} !important`,
      },

      '.cm-tooltip-autocomplete > ul > li[role="option"].cm-empty-option': {
        visibility: 'hidden',
        height: 0,
        overflow: 'hidden',
        fontSize: 0,
        lineHeight: 0,
        padding: '0 !important',
        margin: 0,
      },

      '.cm-completionLabel': {
        fontSize: isSizeDefault
          ? Typography.Code.Base.Default.Size
          : Typography.Code.Small.Default.Size,
        fontFamily: isSizeDefault
          ? Typography.Code.Base.Default.Family
          : Typography.Code.Small.Default.Family,
        color: Colors.Text.Neutral.Default,
        fontWeight: Typography.Code.Base.Default.Weight,
        lineHeight: Typography.Code.Base.Default.LineHeight,
        textDecoration: Typography.Code.Base.Default.TextDecoration,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        textTransform: Typography.Code.Base.Default.TextTransform as any,
        flexGrow: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },

      '.cm-tooltip.cm-tooltip-hover': {
        minWidth: 'auto',
      },

      '.cm-tooltip.cm-tooltip-autocomplete > ul': {
        maxHeight: '20em !important',
      },

      '.cm-tooltip.cm-tooltip-autocomplete > ul > li': {
        position: 'relative',
      },

      '.cm-tooltip.cm-tooltip-autocomplete > ul > li::after': {
        position: 'absolute',
        /* Required or else the outline would be cut off by the listbox boundaries */
        inset: Spacings.Size2,
        borderRadius: 'inherit',
        content: ' ',
      },

      '.cm-tooltip.cm-tooltip-autocomplete li': {
        borderRadius: Borders.Radius.Field.Default,
      },

      '.cm-tooltip.cm-tooltip-autocomplete li[aria-selected]': {
        boxShadow: `inset 0 0 0 ${Borders.Width.Emphasized} ${Colors.Border.Neutral.Accent}, inset 0 0 0 calc(${Borders.Width.Emphasized} + 1px) ${Colors.Border.Neutral.Default}`,
        backgroundColor: 'transparent',
        color: Colors.Text.Neutral.Default,
      },

      '.cm-tooltip.cm-tooltip-autocomplete li[aria-selected]:focus-visible': {
        outline: 'none',
      },

      '.cm-tooltip.cm-completionInfo': {
        padding: `calc(${Spacings.Size4} + ${Spacings.Size6}) ${Spacings.Size16} !important`,
        marginLeft: Spacings.Size4,
        marginRight: Spacings.Size4,
        maxWidth: '500px',
        minWidth: '200px',
      },

      '.cm-tooltip-autocomplete > ul > li.cm-completionOption-action > div': {
        width: '100%',
      },

      '.cm-completionOption-action .cm-completionLabel': {
        display: 'none',
      },

      '.cm-tooltip.cm-tooltip-autocomplete li:hover': {
        backgroundColor: `${Colors.Background.Field.Neutral.DefaultHover}`,
      },
    }),
  ];
};

/**
 * @internal
 */
export const getSingleLineDefaultConfiguration = (
  intlProps: IntlProps,
  options: { required: boolean; size: 'default' | 'condensed' },
): Extension[] => {
  return [
    EditorState.transactionFilter.of((tr) => (tr.newDoc.lines > 1 ? [] : [tr])),
    ...getDefaultConfiguration(intlProps, options),
  ];
};

/**
 * @public
 */
export type GutterConfiguration = {
  lineNumber: number;
  marker: ReactNode;
};

/**
 * @internal
 */
export const getMultipleLinesDefaultConfiguration = (
  intlProps: IntlProps,
  options: {
    gutterConfiguration?: GutterConfiguration;
    required: boolean;
    size: 'default' | 'condensed';
  },
): Extension[] => {
  const { required, gutterConfiguration, size } = options;

  return [
    lineNumbers(),
    foldGutter({
      markerDOM: (open) => {
        const element = document.createElement('div');
        element.style.display = 'flex';
        element.style.alignItems = 'center';
        element.style.marginTop = '2px';
        element.style.transform = 'rotate(-90deg)';
        element.style.cursor = 'pointer';
        element.style.minWidth = '16px';
        element.style.minHeight = '16px';

        if (open) {
          element.style.transform = 'rotate(0deg)';
        }

        const root = createRoot(element);
        root.render(
          <ChevronDownSmallIcon
            size="small"
            style={{ width: Spacings.Size16, height: Spacings.Size16 }}
          />,
        );

        return element;
      },
    }),
    gutter({
      lineMarker: (view, line) => {
        const length = view.state.doc.length;
        if (length === 0 || !gutterConfiguration) {
          return null;
        }
        const { lineNumber, marker } = gutterConfiguration;
        const { from, to } = view.state.doc.line(lineNumber);
        return line.from === from && line.to === to
          ? new (class extends GutterMarker {
              toDOM() {
                const element = document.createElement('div');
                const root = createRoot(element);
                root.render(marker);
                return element;
              }
            })()
          : null;
      },
    }),
    ...getDefaultConfiguration(intlProps, { required, size }),
  ];
};
